import { useQueryClient } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { useApi } from "~/api/provider";
import { Button } from "~/foundation/components/Button";
import { Text } from "~/foundation/components/Text";
import { type RunOutcome, recordCommand, runCommandAction } from "./commandApi";
import {
  anyDanger,
  initialSelection,
  planReady,
  selectedActions,
  summarizeActions,
} from "./commandModel";
import type { CommandAction, CommandProposal, PlanDraft } from "./commandTypes";
import { PlanDraftEditor } from "./PlanDraftEditor";

// One proposal, read and approved by a person (2026-10-21). Owner: "all actions that are done should be approved by
// the user — a popup of are you sure with the relevant buttons. all changes should be listed in a simplified list in a
// tldr manner." So: the TL;DR line first; every proposed change as a tickable row in plain words; then an explicit
// "Are you sure?" step that lists exactly what will run; then what actually happened, one line each, which is also
// what the ledger keeps. A plan (a new work item) is edited in place before it goes.

type Step = "pick" | "confirm" | "running" | "done";

export function CommandProposalCard({
  proposal,
  onDone,
  onDiscard,
  onApplied,
}: {
  proposal: CommandProposal;
  /** The card is finished (changes made, or an answer read). */
  onDone: () => void;
  onDiscard: () => void;
  onApplied?: (receiptId: string | null) => Promise<void>;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<string>>(() => initialSelection(proposal));
  const [drafts, setDrafts] = useState<Record<string, PlanDraft>>(() =>
    Object.fromEntries(
      proposal.actions.filter((a) => a.plan).map((a) => [a.id, a.plan as PlanDraft]),
    ),
  );
  const [step, setStep] = useState<Step>("pick");
  const [results, setResults] = useState<RunOutcome[]>([]);

  const chosen = selectedActions(proposal, selected);
  const ready = chosen.filter((a) => a.kind !== "plan" || planReady(drafts[a.id] ?? a.plan));
  const danger = anyDanger(ready);

  const runAll = async () => {
    setStep("running");
    const out: RunOutcome[] = [];
    for (const action of ready) {
      out.push(await runCommandAction(api, action, proposal.text, drafts[action.id]));
    }
    setResults(out);
    try {
      const receiptId = await recordCommand(api, proposal, out);
      await onApplied?.(receiptId);
    } catch {
      // The action already ran. Let a conversation surface prevent an accidental replay.
      await onApplied?.(null);
    }
    await queryClient.invalidateQueries({ queryKey: ["work"] });
    const ok = out.filter((o) => o.ok).length;
    if (ok === out.length) toast.success(t("Done — {{count}} change(s) made", { count: ok }));
    else
      toast.error(
        t("{{failed}} of {{total}} changes did not go through", {
          failed: out.length - ok,
          total: out.length,
        }),
      );
    setStep("done");
  };

  const toggle = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="flex flex-col gap-3 border-divider border-t p-3" data-testid="command-proposal">
      {proposal.tldr ? (
        <Text variant="body" className="font-semibold">
          {proposal.tldr}
        </Text>
      ) : null}

      {proposal.kind === "answer" ? (
        <>
          <p className="whitespace-pre-wrap text-body text-text">{proposal.answer}</p>
          <div className="flex justify-end">
            <Button size="sm" variant="primary" onClick={onDone}>
              {t("Thanks")}
            </Button>
          </div>
        </>
      ) : null}

      {proposal.kind === "other" ? (
        <>
          <Text variant="small">{t("That is not something I can do on the work board.")}</Text>
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={onDiscard}>
              {t("Close")}
            </Button>
          </div>
        </>
      ) : null}

      {proposal.kind === "unclear" ? (
        <>
          {proposal.questions.map((q) => (
            <Text key={q} variant="small">
              {q}
            </Text>
          ))}
          {proposal.candidates.length > 0 ? (
            <Text variant="small" className="text-text-tertiary">
              {t("Items I can see:")} {proposal.candidates.map((c) => c.key).join(", ")}
            </Text>
          ) : null}
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" onClick={onDiscard}>
              {t("Close")}
            </Button>
          </div>
        </>
      ) : null}

      {proposal.kind === "actions" && step === "pick" ? (
        <>
          {proposal.questions.length > 0 ? (
            <ul className="flex flex-col gap-1">
              {proposal.questions.map((q) => (
                <li key={q} className="text-small text-text-secondary">
                  {q}
                </li>
              ))}
            </ul>
          ) : null}
          <ul className="flex flex-col gap-2" aria-label={t("Proposed changes")}>
            {proposal.actions.map((action) => (
              <li
                key={action.id}
                className="rounded-md border border-divider p-2"
                data-testid={`command-action-${action.id}`}
              >
                <label className="flex cursor-pointer items-start gap-2">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={selected.has(action.id)}
                    onChange={() => toggle(action.id)}
                    aria-label={action.label}
                  />
                  <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                    <span className="flex flex-wrap items-center gap-2">
                      <span
                        className={`font-medium text-body ${action.danger ? "text-danger" : "text-text"}`}
                      >
                        {action.label}
                      </span>
                      {action.target ? (
                        <Link
                          to={action.target.url as never}
                          className="font-mono text-small text-text-secondary underline"
                        >
                          {action.target.key}
                        </Link>
                      ) : null}
                      {action.danger ? (
                        <span className="rounded-full bg-danger/10 px-1.5 text-danger text-small">
                          {t("cannot be undone")}
                        </span>
                      ) : null}
                    </span>
                    <span className="text-small text-text-secondary">{action.description}</span>
                  </span>
                </label>
                {action.kind === "plan" && selected.has(action.id) ? (
                  <div className="mt-2 border-divider border-t pt-2">
                    <PlanDraftEditor
                      draft={drafts[action.id] ?? (action.plan as PlanDraft)}
                      projects={proposal.projects}
                      onChange={(next) => setDrafts((prev) => ({ ...prev, [action.id]: next }))}
                    />
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={onDiscard}>
              {t("Discard")}
            </Button>
            <Button
              size="sm"
              variant={danger ? "danger" : "primary"}
              disabled={ready.length === 0}
              onClick={() => setStep("confirm")}
              data-testid="command-do"
            >
              {ready.length === 1 ? t("Do it") : t("Do these {{count}}", { count: ready.length })}
            </Button>
          </div>
        </>
      ) : null}

      {step === "confirm" ? (
        <div
          role="alertdialog"
          aria-label={t("Are you sure?")}
          className={`flex flex-col gap-2 rounded-md border p-3 ${danger ? "border-danger" : "border-divider"}`}
        >
          <Text variant="body" className="font-semibold">
            {t("Are you sure?")}
          </Text>
          <Text variant="small">{t("This will:")}</Text>
          <ul className="list-disc ps-5 text-small text-text">
            {ready.map((a) => (
              <li key={a.id}>
                {a.kind === "plan" ? planLine(a, drafts[a.id] ?? a.plan, t) : a.label}
              </li>
            ))}
          </ul>
          {danger ? (
            <Text variant="small" className="text-danger">
              {t("Some of these cannot be undone.")}
            </Text>
          ) : null}
          <div className="flex items-center justify-end gap-2">
            <Button size="sm" variant="ghost" onClick={() => setStep("pick")}>
              {t("Not now")}
            </Button>
            <Button
              size="sm"
              variant={danger ? "danger" : "primary"}
              onClick={() => void runAll()}
              data-testid="command-confirm"
            >
              {t("Yes, do it")}
            </Button>
          </div>
        </div>
      ) : null}

      {step === "running" ? <Text variant="small">{t("Working…")}</Text> : null}

      {step === "done" ? (
        <div className="flex flex-col gap-2" data-testid="command-done">
          <Text variant="body" className="font-semibold">
            {t("What changed")}
          </Text>
          <ul className="flex flex-col gap-1">
            {results.map((r) => (
              <li key={r.id} className="flex flex-wrap items-baseline gap-2 text-small">
                <span aria-hidden className={r.ok ? "text-success" : "text-danger"}>
                  {r.ok ? "✓" : "✕"}
                </span>
                <span className="text-text">{r.label}</span>
                {r.filed ? (
                  <Link to={r.filed.url as never} className="font-mono underline">
                    {r.filed.key}
                  </Link>
                ) : null}
                {r.filed ? (
                  <span className="text-text-secondary">
                    {t(
                      "— filed; its flow starts and the developer step goes to the repository's IDE window.",
                    )}
                  </span>
                ) : null}
                {!r.ok && r.error ? <span className="text-danger">{r.error}</span> : null}
              </li>
            ))}
          </ul>
          <Text variant="small" className="text-text-tertiary">
            {t("Kept in the TL;DR list of what AI changed (the TL;DR button).")}
          </Text>
          <div className="flex justify-end">
            <Button size="sm" variant="primary" onClick={onDone}>
              {t("Close")}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function planLine(
  action: CommandAction,
  draft: PlanDraft | null | undefined,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  const d = draft ?? action.plan;
  if (!d) return action.label;
  return t("Create “{{title}}” in {{project}} and send its brief to the IDE", {
    title: d.title,
    project: d.projectName ?? d.projectKey ?? "?",
  });
}

export { summarizeActions };
